import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isBefore,
  isSameDay,
  isSameMonth,
  setDefaultOptions,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { filter, interval, map, startWith } from 'rxjs';

import { TooltipComponent } from '@gfe/shared/components/tooltip/tooltip.component';

import { ICONS } from '@core/services/icons/icons.service';
import { isNil } from '@core/utils/shared-utils';

import {
  ModalComponent,
  ModalConfig,
  ModalType
} from '@shared/components/modal/modal.component';

import { CalendarEventComponent } from '@features/components/visualization/calendar/calendar-event/calendar-event.component';
import { CalendarLegendComponent } from '@features/components/visualization/calendar/calendar-legend/calendar-legend.component';
import { CalendarTimeStepComponent } from '@features/components/visualization/calendar/calendar-time-step/calendar-time-step.component';
import {
  CalendarEvent,
  CalendarEventChange,
  CalendarEventChangeWithScope,
  Hour,
  MonthDay,
  MonthMarker,
  PossibleViews,
  RecurrenceEditScope,
  TimeGridDay
} from '@features/components/visualization/calendar/models/calendar.models';
import { cutoffOffsetPx } from '@features/components/visualization/calendar/utils/calendar-cutoff.utils';
import {
  DAY_HEIGHT_PX,
  HOUR_PX,
  MINUTES_IN_DAY,
  PIXELS_PER_MINUTE,
  columnLayout,
  daySegment,
  eventColor,
  eventInk,
  formatTimeRange,
  tintFill
} from '@features/components/visualization/calendar/utils/calendar-layout.utils';

/** How many event pills a month cell shows before collapsing the rest into `+n`. */
const MONTH_MARKERS_SHOWN = 3;

/** Hours of context kept above `now` when the time grid first scrolls into place. */
const SCROLL_LEAD_HOURS = 3;

/** Largest share of a short pane that lead context may take before the marker suffers. */
const MAX_LEAD_RATIO = 0.4;

/** Used when the entity layer produced no colour for the event. */
const FALLBACK_COLOR = '#4e4c4c';

setDefaultOptions({ locale: fr });

@Component({
  selector: 'app-calendar',
  templateUrl: 'calendar.component.html',
  styleUrl: './calendar.component.css',
  providers: [TranslateService],
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    CalendarEventComponent,
    CalendarLegendComponent,
    CalendarTimeStepComponent,
    TooltipComponent,
    ModalComponent
  ]
})
export class CalendarComponent {
  events = input.required<Omit<CalendarEvent[], 'position'>>();
  defaultView = input<PossibleViews>(PossibleViews.WEEK);

  /** Emitted whenever the user drags or resizes an event; wire to backend when ready. */
  eventChange = output<CalendarEventChangeWithScope>();

  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );
  protected readonly locale = computed(() => this.currentLang() || 'en');
  protected readonly PossibleViews = PossibleViews;
  protected readonly ICONS = ICONS;

  /**
   * Vertical scale of the day and week grids. Fixed rather than measured: the
   * grid scrolls inside its pane, so a narrow split no longer squashes 24 hours
   * into a few hundred pixels and every placement is a pure function of the
   * clock.
   */
  protected readonly pixelsPerMinute = PIXELS_PER_MINUTE;
  protected readonly dayHeightPx = DAY_HEIGHT_PX;
  protected readonly hourPxCss = `${HOUR_PX}px`;

  protected readonly viewSegments = computed(() => {
    this.currentLang(); // Trigger reactivity
    return [PossibleViews.DAY, PossibleViews.WEEK, PossibleViews.MONTH].map((view) => ({
      view,
      label: this.translate.instant(`common.calendar.${view}`)
    }));
  });

  protected readonly recurrenceEditModalOpen = signal<boolean>(false);
  /** Selected edit scope in the recurring-event modal. */
  protected readonly recurrenceScope = signal<RecurrenceEditScope>('exception');
  protected readonly recurrenceScopeOptions: {
    value: RecurrenceEditScope;
    label: string;
    description: string;
    icon: string;
    disabled?: boolean;
  }[] = [
    {
      value: 'exception',
      label: 'common.calendar.edit_recurrence_modal.scope_exception',
      description: 'common.calendar.edit_recurrence_modal.scope_exception_desc',
      icon: ICONS.calendarCheck
    },
    {
      value: 'recurring',
      label: 'common.calendar.edit_recurrence_modal.scope_recurring',
      description: 'common.calendar.edit_recurrence_modal.scope_recurring_desc',
      icon: ICONS.arrowsClockwise
    },
    {
      value: 'next',
      label: 'common.calendar.edit_recurrence_modal.scope_next',
      description: 'common.calendar.edit_recurrence_modal.scope_next_desc',
      icon: ICONS.arrowRight
    }
  ];
  /** Pending drag/resize change awaiting the user's scope choice. */
  private pendingChange: CalendarEventChange | null = null;

  // Make first new Date value
  protected readonly actualDate = toSignal(
    interval(1000).pipe(
      startWith(() => new Date()),
      map(() => new Date())
    )
  ) as Signal<Date>;

  /** Tracks `defaultView` so the container can steer the initial view. */
  protected activeView = linkedSignal(() => this.defaultView());
  protected selectedDate = signal<Date>(new Date());

  /**
   * Whether any block is being dragged. Held here rather than in each block so
   * the others can recede while one is being moved or resized.
   */
  protected readonly dragActive = signal(false);

  private readonly gridScroll = viewChild<ElementRef<HTMLElement>>('gridScroll');
  /**
   * Grid panes already scrolled to `now`, so it happens once per pane. Keyed on
   * the element rather than on the view: the pane is shared by the day and week
   * views — scrolling again on a switch between them would throw away wherever
   * the user had scrolled to — and it is destroyed and rebuilt at `scrollTop 0`
   * on the way back from month, which a view-keyed guard would refuse to fix.
   */
  private readonly scrolledGrids = new WeakSet<HTMLElement>();
  /** Watches a pane that had no height yet, so the framing can be retried. */
  private paneResize: ResizeObserver | null = null;

  protected readonly nowTimeStepPosition = computed(() => {
    const now = this.actualDate();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return `${Math.round((minutes / MINUTES_IN_DAY) * DAY_HEIGHT_PX)}px`;
  });

  protected readonly datePreview = computed(() => {
    const date = this.selectedDate();

    switch (this.activeView()) {
      case PossibleViews.DAY:
        return date.toLocaleDateString(this.locale(), {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      case PossibleViews.WEEK:
        return `${startOfWeek(date).toLocaleDateString(this.locale(), {
          month: 'short',
          day: 'numeric'
        })} - ${endOfWeek(date).toLocaleDateString(this.locale(), {
          month: 'short',
          day: 'numeric'
        })}`;
      case PossibleViews.MONTH:
      default:
        return date.toLocaleDateString(this.locale(), {
          month: 'long',
          year: 'numeric'
        });
    }
  });

  protected readonly startDateOfSelectedInterval = computed(() => {
    switch (this.activeView()) {
      case PossibleViews.DAY:
        return startOfDay(this.selectedDate());
      case PossibleViews.WEEK:
        return startOfWeek(this.selectedDate());
      case PossibleViews.MONTH:
      default:
        // Full weeks, so the leading days of the grid carry their events too.
        return startOfWeek(startOfMonth(this.selectedDate()));
    }
  });
  protected readonly endDateOfSelectedInterval = computed(() => {
    switch (this.activeView()) {
      case PossibleViews.DAY:
        return endOfDay(this.selectedDate());
      case PossibleViews.WEEK:
        return endOfWeek(this.selectedDate());
      case PossibleViews.MONTH:
      default:
        return endOfWeek(endOfMonth(this.selectedDate()));
    }
  });
  protected readonly days = computed(() =>
    eachDayOfInterval({
      start: this.startDateOfSelectedInterval(),
      end: this.endDateOfSelectedInterval()
    })
  );
  protected readonly weekDays = computed(() => {
    this.currentLang(); // Trigger reactivity
    const days = this.translate.instant('common.calendar.week_days').split(',');
    const res: string[] = [];
    for (let i = 0; i < days.length; i++) {
      res[i] = days[(this.startWeekDay() + i) % 7];
    }
    return res || [];
  });
  protected readonly startWeekDay = computed(() => startOfWeek(new Date()).getDay());

  // Front-only per-session overrides for dragged/resized events (keyed by uid).
  private overrides = signal<Map<string, { start: Date; end: Date }>>(new Map());

  /** Events with any local drag/resize overrides applied on top of the server data. */
  private appliedEvents = computed<CalendarEvent[]>(() => {
    const ov = this.overrides();
    return this.events().map((e) => {
      const o = ov.get(e.uid);
      return o ? { ...e, start: o.start, end: o.end } : e;
    });
  });

  /** Occurrences falling inside the visible range, recurrences already expanded. */
  private readonly expandedEvents = computed(() =>
    this.expandRecurringEvents(
      this.appliedEvents(),
      this.startDateOfSelectedInterval(),
      this.endDateOfSelectedInterval()
    )
  );

  protected readonly visibleEventCount = computed(() => this.expandedEvents().length);

  protected readonly monthDaysEnriched = computed<MonthDay[]>(() => {
    const events = this.expandedEvents();
    const anchor = this.selectedDate();
    const today = this.actualDate();

    return this.days().map((day) => {
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const markers = events
        .filter((event) => event.start <= dayEnd && dayStart < (event.end || event.start))
        .sort((a, b) => a.start.getTime() - b.start.getTime());

      return {
        day,
        isToday: isSameDay(day, today),
        isOutsideMonth: !isSameMonth(day, anchor),
        isWeekend: this.isWeekend(day),
        visibleMarkers: markers
          .slice(0, MONTH_MARKERS_SHOWN)
          .map((event) => this.toMonthMarker(event)),
        hiddenMarkerCount: Math.max(0, markers.length - MONTH_MARKERS_SHOWN)
      };
    });
  });

  /** Columns of the day or week grid, each carrying its own laid-out events. */
  protected readonly timeGridDays = computed<TimeGridDay[]>(() => {
    const events = this.expandedEvents();
    const today = this.actualDate();
    const isDayView = this.activeView() === PossibleViews.DAY;

    return this.days().map((day, index) => ({
      id: index,
      day,
      isToday: isSameDay(day, today),
      isWeekend: this.isWeekend(day),
      shortLabel: day.toLocaleDateString(this.locale(), {
        weekday: isDayView ? 'long' : 'short'
      }),
      markers: this.getMarkersForDay(day, events)
    }));
  });

  /** Dates of the grid columns, left→right, for cross-day drag geometry. */
  protected readonly columnDates = computed(() => this.timeGridDays().map((d) => d.day));

  protected readonly showNowLabel = computed(() =>
    this.timeGridDays().some((day) => day.isToday)
  );

  protected readonly hoursEnriched = computed<Hour[]>(() =>
    Array.from({ length: 24 }, (_, i) => ({
      hour: `${String(i).padStart(2, '0')}:00`,
      top: i * HOUR_PX,
      // The first label would be clipped by the column's top edge, so it hangs
      // just below the line instead of straddling it like the others.
      labelTop: i === 0 ? 3 : i * HOUR_PX - 6
    }))
  );

  protected readonly modalConfig: ModalConfig = {
    icon: 'arrowsClockwise',
    type: ModalType.INFO,
    dismissible: true,
    closeOnBackdropClick: true,
    actions: [
      {
        label: this.translate.instant('common.cancel'),
        color: 'cancel',
        callback: () => this.cancelRecurrenceEdit()
      },
      {
        label: this.translate.instant('common.ok'),
        color: 'primary',
        callback: () => this.confirmRecurrenceEdit()
      }
    ]
  };

  constructor() {
    effect(() => {
      const oneOffEvents = this.events().filter((event: CalendarEvent) =>
        isNil(event.recurrence)
      );
      if (oneOffEvents.length === 0) return;

      // A dataset that already reaches today stays on today: paging back to its
      // oldest event would drop today's column, and with it the only place the
      // "now" marker can be drawn. Only a dataset that ends before today — or
      // starts after it — is worth opening on its own first day.
      const now = new Date();
      const todayStart = startOfDay(now);
      const todayEnd = endOfDay(now);
      const coversToday = oneOffEvents.some(
        (event) => event.start <= todayEnd && todayStart < (event.end || event.start)
      );
      if (coversToday) return;

      this.selectedDate.set(
        oneOffEvents.reduce((a, b) => (a.start <= b.start ? a : b)).start
      );
    });

    effect(() => {
      const element = this.gridScroll()?.nativeElement;
      if (!element || this.scrolledGrids.has(element)) return;
      if (this.frameGridOnNow(element)) return;

      this.watchUntilPaneOpens(element);
    });

    this.destroyRef.onDestroy(() => this.paneResize?.disconnect());
  }

  /**
   * Scrolls the pane so the "now" marker sits just below its top edge, and
   * reports whether it could. 24 hours are ~912px tall, so a pane left at 00:00
   * shows the middle of the night.
   *
   * A collapsed pane still accepts `scrollTop` and keeps the value once it
   * opens, so being on screen is what separates framing the grid from freezing
   * it: `data-visu-container` builds the calendar inside a collapsed
   * `as-split-area`, hours before the user opens that panel. Framing it then
   * would cache the hour the page loaded rather than the hour it is looked at.
   *
   * Both dimensions are checked because a split collapses an area only along
   * its own direction — the horizontal one wrapping the calendar zeroes the
   * width and leaves a full height behind, which reads as perfectly laid out.
   *
   * The clock is read directly rather than through `actualDate` so the
   * per-second tick does not re-run this.
   */
  private frameGridOnNow(element: HTMLElement): boolean {
    if (element.clientWidth === 0 || element.clientHeight === 0) return false;

    const now = new Date();
    const nowOffset = (now.getHours() * 60 + now.getMinutes()) * PIXELS_PER_MINUTE;
    element.scrollTop = Math.max(0, nowOffset - this.scrollLeadPx(element.clientHeight));
    this.scrolledGrids.add(element);
    return true;
  }

  /**
   * Context kept above the marker. `SCROLL_LEAD_HOURS` costs 114px, which a pane
   * squeezed into a split row cannot spare: at 216px it would push the marker
   * past three quarters of the way down, close enough to the bottom edge that a
   * stale frame drops it off screen entirely. Below that, the lead is what gives
   * way, never the marker.
   */
  private scrollLeadPx(paneHeight: number): number {
    return Math.min(SCROLL_LEAD_HOURS * HOUR_PX, paneHeight * MAX_LEAD_RATIO);
  }

  /** Retries the framing on every resize until the pane is actually on screen. */
  private watchUntilPaneOpens(element: HTMLElement): void {
    this.paneResize?.disconnect();
    this.paneResize = new ResizeObserver(() => {
      if (!this.frameGridOnNow(element)) return;
      this.paneResize?.disconnect();
      this.paneResize = null;
    });
    this.paneResize.observe(element);
  }

  private isWeekend(day: Date): boolean {
    const dow = day.getDay();
    return dow === 0 || dow === 6;
  }

  /** Presentation of one event pill in a month cell. */
  private toMonthMarker(event: CalendarEvent): MonthMarker {
    const color = eventColor(event) ?? FALLBACK_COLOR;
    const range = formatTimeRange(event.start, event.end);

    return {
      event,
      color,
      fill: tintFill(event.backgroundColor ?? FALLBACK_COLOR),
      ink: eventInk(color),
      tooltip: range
    };
  }

  protected changeView(view: PossibleViews) {
    if (this.activeView() !== view) {
      this.activeView.set(view);
    }
  }

  protected switchToPrevious() {
    this.shiftSelectedDate(-1);
  }

  protected switchToNext() {
    this.shiftSelectedDate(1);
  }

  private shiftSelectedDate(direction: 1 | -1): void {
    const current = this.selectedDate();

    switch (this.activeView()) {
      case PossibleViews.DAY:
        this.selectedDate.set(addDays(current, direction));
        break;
      case PossibleViews.WEEK:
        this.selectedDate.set(addWeeks(current, direction));
        break;
      case PossibleViews.MONTH:
        // Paged from the first of the month: `selectedDate` is seeded from an
        // event start, so it routinely sits on a day the next month does not
        // have, and a month step from there would overflow into the month after
        // (31 January + 1 month = 3 March), skipping February entirely.
        this.selectedDate.set(addMonths(startOfMonth(current), direction));
        break;
    }
  }

  protected returnToday() {
    this.selectedDate.set(new Date(startOfToday()));
  }

  private expandRecurringEvents(
    events: CalendarEvent[],
    viewStart: Date,
    viewEnd: Date
  ): CalendarEvent[] {
    const expandedEvents: CalendarEvent[] = [];
    const MAX_ITERATIONS = 1000; // Prevent infinite loops

    for (const event of events) {
      if (!event.recurrence) {
        expandedEvents.push(event);
        continue;
      }

      const recurrence = event.recurrence;
      const eventDuration = event.end.getTime() - event.start.getTime();
      const excluded = new Set(recurrence.excludedDates ?? []);

      // Add original event if it falls within the view range
      const originalStart = startOfDay(event.start);
      const originalEnd = endOfDay(event.end);
      if (
        (originalStart >= startOfDay(viewStart) && originalStart <= endOfDay(viewEnd)) ||
        (originalEnd >= startOfDay(viewStart) && originalEnd <= endOfDay(viewEnd)) ||
        (originalStart < startOfDay(viewStart) && originalEnd > endOfDay(viewEnd))
      ) {
        if (
          !excluded.has(event.start.toISOString().split('T')[0]) &&
          this.isWithinValidityWindow(event.start, recurrence)
        ) {
          expandedEvents.push(event);
        }
      }

      // Start from a date closer to the view range for better performance. The
      // search window is widened by one occurrence length so occurrences that
      // start before the view but are still running when it begins (windows
      // crossing midnight) are still generated.
      let currentDate = this.getOptimalStartDate(
        event.start,
        new Date(viewStart.getTime() - eventDuration),
        recurrence
      );
      let iterations = 0;

      while (
        iterations < MAX_ITERATIONS &&
        isBefore(currentDate, viewEnd) &&
        (!recurrence.endDate || currentDate <= recurrence.endDate) &&
        (!recurrence.validTo || currentDate <= recurrence.validTo)
      ) {
        iterations++;

        // An occurrence that started before the view still has a visible tail on
        // its first day when it runs past midnight, so keep it too.
        const startsInView = !isBefore(currentDate, viewStart);
        const spillsIntoView = currentDate.getTime() + eventDuration > viewStart.getTime();

        if (startsInView || spillsIntoView) {
          const shouldInclude = this.shouldIncludeRecurringEvent(currentDate, recurrence);
          const dateString = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD

          if (
            shouldInclude &&
            !isSameDay(currentDate, event.start) &&
            !excluded.has(dateString) &&
            this.isWithinValidityWindow(currentDate, recurrence)
          ) {
            expandedEvents.push({
              ...event,
              uid: `${event.uid}_${dateString}`,
              start: new Date(currentDate),
              end: new Date(currentDate.getTime() + eventDuration),
              position: undefined // Reset position for recalculation
            });
          }
        }

        currentDate = this.getNextRecurrenceDate(currentDate, recurrence);
      }
    }

    return expandedEvents;
  }

  /**
   * Whether `date` falls inside the recurrence's optional validity window
   * (`validFrom`/`validTo`, both inclusive). An absent bound means open-ended on
   * that side, so a recurrence with no bounds always returns true.
   */
  private isWithinValidityWindow(
    date: Date,
    recurrence: CalendarEvent['recurrence']
  ): boolean {
    if (!recurrence) return true;
    if (recurrence.validFrom && isBefore(date, recurrence.validFrom)) return false;
    if (recurrence.validTo && isBefore(recurrence.validTo, date)) return false;
    return true;
  }

  private shouldIncludeRecurringEvent(
    date: Date,
    recurrence: CalendarEvent['recurrence']
  ): boolean {
    if (!recurrence) return false;

    const dayOfWeek = date.getDay();

    switch (recurrence.type) {
      case 'Daily':
        return true;
      case 'Weekdays':
        return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday to Friday
      case 'Weekends':
        return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
      case 'CustomDays':
        return recurrence.customDays?.includes(dayOfWeek) || false;
      default:
        return false;
    }
  }

  private getNextRecurrenceDate(
    currentDate: Date,
    recurrence: CalendarEvent['recurrence']
  ): Date {
    if (!recurrence) return currentDate;

    const interval = recurrence.interval || 1;

    switch (recurrence.type) {
      case 'Daily':
        return addDays(currentDate, interval);
      case 'Weekdays':
        return this.getNextWeekdayOccurrence(currentDate, interval);
      case 'Weekends':
        return this.getNextWeekendOccurrence(currentDate, interval);
      case 'CustomDays':
        return this.getNextCustomDayOccurrence(
          currentDate,
          recurrence.customDays || [],
          interval
        );
      default:
        return addDays(currentDate, interval);
    }
  }

  private getNextWeekdayOccurrence(currentDate: Date, interval: number): Date {
    let nextDate = addDays(currentDate, 1);
    let weekdaysSkipped = 0;

    while (weekdaysSkipped < interval) {
      const dayOfWeek = nextDate.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        // Monday to Friday
        weekdaysSkipped++;
        if (weekdaysSkipped < interval) {
          nextDate = addDays(nextDate, 1);
        }
      } else {
        nextDate = addDays(nextDate, 1);
      }
    }

    return nextDate;
  }

  private getNextWeekendOccurrence(currentDate: Date, interval: number): Date {
    let nextDate = addDays(currentDate, 1);
    let weekendsSkipped = 0;

    while (weekendsSkipped < interval) {
      const dayOfWeek = nextDate.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        // Sunday or Saturday
        weekendsSkipped++;
        if (weekendsSkipped < interval) {
          nextDate = addDays(nextDate, 1);
        }
      } else {
        nextDate = addDays(nextDate, 1);
      }
    }

    return nextDate;
  }

  private getNextCustomDayOccurrence(
    currentDate: Date,
    customDays: number[],
    interval: number
  ): Date {
    if (customDays.length === 0) return addDays(currentDate, 1);

    let nextDate = addDays(currentDate, 1);
    let customDaysSkipped = 0;

    while (customDaysSkipped < interval) {
      const dayOfWeek = nextDate.getDay();
      if (customDays.includes(dayOfWeek)) {
        customDaysSkipped++;
        if (customDaysSkipped < interval) {
          nextDate = addDays(nextDate, 1);
        }
      } else {
        nextDate = addDays(nextDate, 1);
      }
    }

    return nextDate;
  }

  private getOptimalStartDate(
    eventStart: Date,
    viewStart: Date,
    recurrence: CalendarEvent['recurrence']
  ): Date {
    if (!recurrence || eventStart >= viewStart) {
      return new Date(eventStart);
    }

    const interval = recurrence.interval || 1;
    const daysBetween = Math.floor(
      (viewStart.getTime() - eventStart.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysBetween <= 0) {
      return new Date(eventStart);
    }

    switch (recurrence.type) {
      case 'Daily': {
        const cycles = Math.floor(daysBetween / interval);
        return addDays(eventStart, cycles * interval);
      }
      case 'Weekdays':
      case 'Weekends':
      case 'CustomDays': {
        // For weekly patterns, estimate by weeks but then fine-tune
        const estimatedWeeks = Math.floor(daysBetween / 7);
        const estimatedStart = addWeeks(eventStart, Math.max(0, estimatedWeeks - 1));
        return estimatedStart;
      }
      default:
        return new Date(eventStart);
    }
  }

  /**
   * Vertical placement of `event` within the column for `day`, clamped to the
   * column's edges so a window crossing midnight is cut rather than overflowing
   * into its neighbour.
   */
  private getBoundingOfEventForWeekDisplay(
    event: CalendarEvent,
    day: Date
  ): { top: number; height: number } {
    const dayStart = startOfDay(day);
    const dayEnd = new Date(dayStart.getTime() + MINUTES_IN_DAY * 60_000);

    const effectiveStart = event.start < dayStart ? dayStart : event.start;
    const effectiveEnd =
      (event.end || event.start) > dayEnd ? dayEnd : event.end || event.start;

    const minutesFromDayStart =
      effectiveStart.getHours() * 60 + effectiveStart.getMinutes();
    const durationInMinutes =
      (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60);

    return {
      top: (minutesFromDayStart / MINUTES_IN_DAY) * DAY_HEIGHT_PX,
      height: (durationInMinutes / MINUTES_IN_DAY) * DAY_HEIGHT_PX
    };
  }

  private getMarkersForDay(day: Date, events?: CalendarEvent[]): CalendarEvent[] {
    const eventsToUse = events || this.events();
    const dayEvents = eventsToUse.filter((event) => {
      const eventEnd = event.end || event.start;
      return event.start <= endOfDay(day) && startOfDay(day) < eventEnd;
    });

    const sortedEvents = dayEvents.sort((a, b) => a.start.getTime() - b.start.getTime());
    const eventGroups = this.groupOverlappingEvents(sortedEvents);

    const generatedEvents: CalendarEvent[] = [];

    for (const group of eventGroups) {
      const eventsWithColumns = this.assignColumnsToEvents(group);

      for (const event of eventsWithColumns) {
        const { top, height } = this.getBoundingOfEventForWeekDisplay(event, day);
        const { width, left } = columnLayout(
          event.position?.column ?? 0,
          event.position?.overlap ?? 1
        );
        const { continuesBefore, continuesAfter } = daySegment(event, day);

        generatedEvents.push({
          ...event,
          position: {
            top,
            height,
            width,
            left,
            column: event.position?.column,
            overlap: event.position?.overlap,
            cutoffOffset: cutoffOffsetPx(event, day, DAY_HEIGHT_PX),
            continuesBefore,
            continuesAfter
          }
        });
      }
    }

    return generatedEvents;
  }

  private assignColumnsToEvents(columns: CalendarEvent[]): CalendarEvent[] {
    const sortedEvents = columns.sort((a, b) => a.start.getTime() - b.start.getTime());
    const columnsArray: CalendarEvent[][] = [];

    for (const event of sortedEvents) {
      let assignedColumn = -1;

      for (let i = 0; i < columnsArray.length; i++) {
        const canFitInColumn = !columnsArray[i].some((e) =>
          this.isEventOverlapping(e, event)
        );

        if (canFitInColumn) {
          assignedColumn = i;
          break;
        }
      }

      if (assignedColumn === -1) {
        assignedColumn = columnsArray.length;
        columnsArray.push([]);
      }

      columnsArray[assignedColumn].push(event);
    }

    return sortedEvents.map((e) => ({
      ...e,
      position: {
        ...e.position,
        column: columnsArray.findIndex((col) => col.includes(e)),
        overlap: columnsArray.length
      }
    }));
  }

  private groupOverlappingEvents(events: CalendarEvent[]): CalendarEvent[][] {
    const groups: CalendarEvent[][] = [];

    for (const event of events) {
      let placed = false;
      for (const group of groups) {
        if (group.some((e) => this.isEventOverlapping(e, event))) {
          group.push(event);
          placed = true;
          break;
        }
      }
      if (!placed) {
        groups.push([event]);
      }
    }

    return this.mergeIndirectlyOverlappingGroups(groups);
  }

  private mergeIndirectlyOverlappingGroups(groups: CalendarEvent[][]): CalendarEvent[][] {
    let merged = true;

    while (merged) {
      merged = false;
      for (let i = 0; i < groups.length - 1; i++) {
        for (let j = i + 1; j < groups.length; j++) {
          if (this.doGroupsOverlap(groups[i], groups[j])) {
            groups[i] = [...groups[i], ...groups[j]];
            groups.splice(j, 1);
            merged = true;
            break;
          }
        }
        if (merged) break;
      }
    }

    return groups;
  }

  private doGroupsOverlap(group1: CalendarEvent[], group2: CalendarEvent[]): boolean {
    return group1.some((event1) =>
      group2.some((event2) => this.isEventOverlapping(event1, event2))
    );
  }

  private isEventOverlapping(event1: CalendarEvent, event2: CalendarEvent): boolean {
    if (event1 === event2) return false;
    return event1.start < event2.end && event2.start < event1.end;
  }

  protected trackMonthDay(index: number, item: MonthDay): string {
    return `${item.day.getTime()}_${index}`;
  }

  protected trackMonthMarker(_index: number, item: MonthMarker): string {
    return `${item.event.uid}_${item.event.start.getTime()}`;
  }

  protected trackWeekDay(_index: number, item: TimeGridDay): string {
    return `${item.id}_${item.day.getTime()}`;
  }

  protected trackHour(index: number, item: Hour): string {
    return `${item.hour}_${index}`;
  }

  protected trackCalendarEvent(_index: number, item: CalendarEvent): string {
    return `${item.uid}_${item.start.getTime()}`;
  }

  protected trackWeekDayString(index: number, item: string): string {
    return `${item}_${index}`;
  }

  /**
   * Handles a drag/resize change emitted by app-calendar-event.
   * Stores the new times as a front-only override and forwards to the public output.
   */
  protected onEventChange(change: CalendarEventChange, recurrence: boolean): void {
    if (recurrence) {
      // Recurring event: ask the user for the edit scope (Google Calendar style).
      this.pendingChange = change;
      this.recurrenceScope.set('exception');
      this.recurrenceEditModalOpen.set(true);
    } else {
      this.sendChangeEvent(change);
    }
  }

  /** Close the recurrence modal without applying the pending change. */
  protected cancelRecurrenceEdit(): void {
    this.pendingChange = null;
    this.recurrenceEditModalOpen.set(false);
  }

  /**
   * Apply the pending change with the scope selected in the modal.
   * Front-only for now: the dragged occurrence is updated locally and emitted.
   * The chosen scope (`exception` / `recurring`), read from `recurrenceScope()`,
   * is what the future backend (GBE) wiring will use to either create a single
   * exception or update the whole recurring series.
   */
  protected confirmRecurrenceEdit(): void {
    const change = this.pendingChange;
    const scope = this.recurrenceScope();
    this.recurrenceEditModalOpen.set(false);
    this.pendingChange = null;
    if (!change) return;
    this.sendChangeEvent(change, scope);
  }

  private sendChangeEvent(event: CalendarEventChange, scope?: RecurrenceEditScope): void {
    const next = new Map(this.overrides());
    next.set(event.uid, { start: event.start, end: event.end });
    this.overrides.set(next);
    this.eventChange.emit({ ...event, scope });
  }
}
